import { assoc, map, pipe } from 'ramda';
import {
  AnySection,
  SectionBase,
  SectionType,
  PanelDesign,
  PanelDesignBase,
  BaseClientEntities,
  applyOverridesFactory,
} from '@wix/add-panel-common';
import type { Utils } from '../../AddPanel/UtilsProvider';
import type { EvaluateCondition } from '../../AddPanel/utils/evaluateCondition';
import { sortByIndex } from '../array';
import { transformItems } from './items';
import { transformSections } from './sections';
import { filterEntitiesWithChildren, filterOutEntitiesByConditions } from './common';

const applyOverrides = applyOverridesFactory({ isApplyingOverridesOnClient: true });
const isDesignPanelSection = (section: SectionBase): boolean =>
  section.type === SectionType.Theme ||
  section.type === SectionType.Preset ||
  section.type === SectionType.Personalized ||
  section.type === SectionType.PersonalizedDesign;

const isTheSameSystemChildId = (aItemSystemId: string, bItemSystemId: string) => {
  const [aSystemId] = aItemSystemId.split('+');
  const [bSystemId] = bItemSystemId.split('+');

  return aSystemId === bSystemId;
};

export const addSections = (allSections: AnySection[], panelDesigns: PanelDesignBase[]) => {
  const sectionsByPanelDesigns = new Map<string, AnySection[]>(
    panelDesigns.map((panelDesign) => [panelDesign.systemId, []]),
  );
  for (const section of allSections) {
    if (section.panelDesignIds) {
      for (const panelDesignId of section.panelDesignIds) {
        // Check for duplicates
        const currentItems = sectionsByPanelDesigns.get(panelDesignId);

        if (!currentItems?.find((item) => isTheSameSystemChildId(section.systemId, item.systemId))) {
          currentItems?.push(section);
        }
      }
    }
  }
  return (panelDesign: PanelDesignBase): PanelDesign => {
    const sectionsForPanelDesign = sectionsByPanelDesigns.get(panelDesign.systemId) || [];
    return assoc('sections', sectionsForPanelDesign, panelDesign);
  };
};

export const transformPanelDesigns = (
  panelDesigns: PanelDesignBase[],
  sectionsWithItems: AnySection[],
): PanelDesign[] =>
  pipe<PanelDesignBase[], PanelDesignBase[], PanelDesign[]>(
    sortByIndex,
    map(addSections(sectionsWithItems, panelDesigns)),
  )(panelDesigns);

export const transformEntitiesToPanelDesigns = ({
  entities,
  evaluateCondition,
  hasSectionStyle,
}: {
  entities: BaseClientEntities;
  evaluateCondition: EvaluateCondition;
  hasSectionStyle: Utils['hasSectionStyle'];
}): { panelDesigns: PanelDesign[]; sections: SectionBase[] } => {
  const {
    sections: _sections = [],
    items: _items = [],
    sectionsOverrides = [],
    itemsOverrides = [],
    panelDesigns: _panelDesigns = [],
  } = entities;

  const filterOutEntities = filterOutEntitiesByConditions(evaluateCondition);

  const filteredPanelDesigns = filterOutEntities(_panelDesigns);
  const filteredSections = filterOutEntities(_sections);
  const filteredSectionsOverrides = filterOutEntities(sectionsOverrides);
  const filteredItems = filterOutEntities(_items);
  const filteredItemsOverrides = filterOutEntities(itemsOverrides);

  // apply panelDesign
  // entity = panelDesign
  // child = section
  const [panelDesigns, panelDesignSections] = applyOverrides({
    entityList: filteredPanelDesigns,
    overridesList: [],
    overrideToEntityReferenceFieldName: '',
    childEntityList: filteredSections,
    childToEntityMultiReferenceFieldName: 'panelDesignIds',
    childToOverrideMultiReferenceFieldName: 'panelDesignOverrideIds',
  });

  // apply panelDesign section overrides
  // parent = panelDesign
  // entity = section
  // child = items
  const [sections, panelDesignItems] = applyOverrides({
    entityList: panelDesignSections,
    overridesList: filteredSectionsOverrides,
    overrideToEntityReferenceFieldName: 'sectionToOverride',
    childEntityList: filteredItems,
    childToEntityMultiReferenceFieldName: 'sectionIds',
    childToOverrideMultiReferenceFieldName: 'sectionOverrideIds',
  });

  // apply panelDesign items overrides
  // parent = section
  // entity = item
  const [items] = applyOverrides({
    entityList: panelDesignItems,
    overridesList: filteredItemsOverrides,
    overrideToEntityReferenceFieldName: 'itemToOverride',
    childEntityList: [],
    childToEntityMultiReferenceFieldName: '',
    childToOverrideMultiReferenceFieldName: '',
    entityToParentMultiReferenceFieldName: 'sectionIds',
  });

  const transformedItems = transformItems(items);

  const sectionsWithItems = transformSections(sections, transformedItems, { hasSectionStyle });

  const panelDesignsWithSections = transformPanelDesigns(panelDesigns, sectionsWithItems);

  return {
    panelDesigns: panelDesignsWithSections,
    sections: sectionsWithItems.filter(isDesignPanelSection),
  };
};
